import React from "react";
import {
  Box,
  Flex,
  Stack,
  Heading,
  Text,
  Button,
  Image,
  useColorModeValue,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, Brain, BarChart3 } from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();

  const bgColor = useColorModeValue("gray.50", "gray.50");
  const textColor = useColorModeValue("gray.700", "gray.700");
  const accentColor = useColorModeValue("teal.500", "teal.500");
  const cardBg = useColorModeValue("white", "gray.50");

  // Feature card data for easy mapping
  const features = [
    {
      title: "AI-Powered Insights",
      description:
        "Understand market sentiment instantly — powered by GPT-driven analysis and real-time data.",
      image: "/images/NewsAnalyzer.png",
      icon: <Brain size={36} color="white" />,
    },
    {
      title: "Live Market Trends",
      description:
        "Track global indices, crypto, and volatility (VIX) to understand the current market tone.",
      image: "/images/Market_sentiment.png",
      icon: <TrendingUp size={36} color="white" />,
    },
    {
      title: "Personalized Dashboard",
      description:
        "Stay organized with a tailored watchlist, stock analysis, and mood tracking — all in one place.",
      image: "/images/WatchList.png",
      icon: <BarChart3 size={36} color="white" />,
    },
  ];

  return (
    <Box bg={bgColor} minH="100vh" px={{ base: 4, md: 8 }} py={{ base: 6, md: 12 }}>
      <Box className="container">
        {/* Hero Section */}
        <Flex
          direction={{ base: "column", md: "row" }}
          align="center"
          justify="space-between"
          mb={{ base: 12, md: 20 }}
        >
          <Box flex="1" textAlign={{ base: "center", md: "left" }}>
            <Heading
              as="h1"
              fontSize={{ base: "3xl", md: "5xl" }}
              fontWeight="bold"
              mb={4}
              color={accentColor}
            >
              TickerMood
            </Heading>
            <Text fontSize={{ base: "lg", md: "xl" }} color={textColor} mb={6}>
              Decode market mood with AI — real-time sentiment, stock insights, and news summaries at your fingertips.
            </Text>
            <Stack
              direction={{ base: "column", sm: "row" }}
              spacing={4}
              justify={{ base: "center", md: "flex-start" }}
            >
              <Button
                colorScheme="teal"
                size="lg"
                w={{ base: "full", sm: "auto" }}
                onClick={() => navigate("/login")}
              >
                Get Started
              </Button>
              <Button
                variant="outline"
                colorScheme="teal"
                size="lg"
                w={{ base: "full", sm: "auto" }}
                onClick={() => navigate("/signup")}
              >
                Sign Up Free
              </Button>
            </Stack>
          </Box>

          <Box flex="1" mt={{ base: 10, md: 0 }} textAlign="center">
            <Image
              src="/images/tickermood.png"
              alt="TickerMood App Preview"
              maxW={{ base: "90%", md: "80%" }}
              mx="auto"
              borderRadius="xl"
              boxShadow="xl"
              objectFit="contain"
            />
          </Box>
        </Flex>

        {/* Feature Highlights */}
        <Stack
          direction={{ base: "column", md: "row" }}
          spacing={{ base: 6, md: 8 }}
          justify="center"
          align="stretch"
          mt={{ base: 8, md: 12 }}
        >
          {features.map((feature, idx) => (
            <Box
              key={idx}
              bg={cardBg}
              borderRadius="xl"
              boxShadow="md"
              p={6}
              textAlign="center"
              position="relative"
              overflow="visible" // ensure icon circle is visible
            >
              {/* Icon Circle with Hover Animation */}
              <Box
                position="absolute"
                top="-24px"
                left="50%"
                transform="translateX(-50%)"
                bg={accentColor}

                borderRadius="full"
                p={4}
                boxShadow="md"
                display="flex"
                justifyContent="center"
                alignItems="center"
                transition="all 0.3s ease"
                _hover={{
                  transform: "translateX(-50%) translateY(-4px) scale(1.1)",
                  boxShadow: "lg",
                }}
              >
                {feature.icon}
              </Box>

              <Heading size="md" mt={6} mb={2}>
                {feature.title}
              </Heading>
              <Text fontSize="sm" color={textColor}>
                {feature.description}
              </Text>
              <Image
                src={feature.image}
                alt={feature.title}
                maxW={{ base: "90%", md: "80%" }}
                mx="auto"
                borderRadius="xl"
                boxShadow="xl"
                objectFit="contain"
                mt={4}
              />
            </Box>
          ))}
        </Stack>

        {/* Footer CTA */}
        <Box textAlign="center" mt={{ base: 10, md: 16 }} pb={{ base: 12, md: 0 }}>
          <Text fontSize="sm" color="gray.500" mb={3}>
            Ready to analyze the market mood?
          </Text>
          <Button colorScheme="teal" size="lg" onClick={() => navigate("/login")}>
            Launch TickerMood
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default LandingPage;
